import React, { useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { mergeDeepRight, mergeRight, remove } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import EditIcon from "@mui/icons-material/Edit";
import LibraryOptionsDialog from "../edit/LibraryOptionsDialog";
import { caseInsensitiveSort } from "../../util/sorts";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const sortLibraryOptions = caseInsensitiveSort("libraryName");

const LibraryOptionsCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      setDialogOpen(false);
      const update = mergeRight(project, {
        libraryOptions: data,
      });
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const update = mergeDeepRight(project, {
        libraryOptions: remove(
          idx,
          1,
          sortLibraryOptions(project.libraryOptions),
        ),
      });

      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          color: "primary",
          variant: "h6",
        }}
        title="Library Options"
        action={
          <Tooltip title="Add Library Options">
            <IconButton onClick={handleAddClick} size="large">
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Library Options.
          </Box>
        )}
        {isBlank(project.libraryOptions) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Categories are set up for this Project.
          </Box>
        )}
        {notBlank(project.libraryOptions) && (
          <List dense style={{ maxHeight: 400, overflowY: "auto" }}>
            {mapIndexed(
              (lib, idx) => (
                <ListItem key={lib.localLibraryOptionId}>
                  <ListItemText primary={lib.libraryName} />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(idx)}
                      dialogTitle="Remove Library Option"
                      dialogBody="Are you sure you want to remove this library option?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              sortLibraryOptions(project.libraryOptions),
            )}
          </List>
        )}
      </CardContent>
      {dialogOpen && (
        <LibraryOptionsDialog
          project={project}
          open={dialogOpen}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default LibraryOptionsCard;
